import { enqueueLoadUpdate, resetLoadUpdateBatch } from '../utils/load-update-batch'
import { handleLoadNotesUpdate, resetLoadNotesSync } from '../utils/load-notes-sync'
import {
    IUpdaterPayrollPlacementsUpdateMessage,
    IUpdaterLoadProcessingUpdateMessage,
    IUpdaterRateconProcessingUpdateMessage,
    IUpdaterTripUpdateMessage,
    UpdaterActionTypes,
    UpdaterBaseMessage,
    UpdaterTripUpdateTypes,
} from '@numeo/types'
import { WsMessageCategory } from '@numeo/types-core'
import { AppDispatch, RootState } from 'store'
import { addTrips, setLoadProcessingStatus, setShouldFetchLoads, setTabTripIds } from 'store/loadsSlice'
import { dismissSnackbar, showError, showInfo, showSuccess } from 'utils/snackbar'
import { setDriversDispatchStatus } from 'store/schedulerSlice'
import { handleDriverStatusDelta } from './boardUpdate'

import { applyDriverNoteUpdate, invalidateDriverNotes } from '@/services/driverNoteCache'

const UPDATER_TASK_UPDATE_ACTION = 'updater.taskUpdate'
const NON_ELD_PROCESSING_MESSAGE_TOKEN = 'without ELD integration'

// Track processingId → snackbar ID so we can dismiss "processing" when result arrives
const rateconProcessingSnackbars = new Map<string, string>()

interface UpdaterTaskUpdateMessage {
    action: typeof UPDATER_TASK_UPDATE_ACTION
    loadId: string
    taskId?: string
    updateType: 'taskCreated' | 'taskUpdated' | 'taskStatusChanged' | 'taskDeleted'
    status?: string
    timestamp?: number
}

const shouldShowNoEldUpdateToast = (status: IUpdaterLoadProcessingUpdateMessage['status'], message: string | undefined): message is string => {
    return status === 'error' && typeof message === 'string' && message.includes(NON_ELD_PROCESSING_MESSAGE_TOKEN)
}

export const resetBatchState = (): void => {
    resetLoadUpdateBatch()
    resetLoadNotesSync()
}

export const handleUpdaterMessages = (message: UpdaterBaseMessage, dispatch: AppDispatch, getState: () => RootState): boolean => {
    if (message.categoryType !== WsMessageCategory.Updater) return false

    try {
        const action = (message.data as { action?: string } | undefined)?.action
        const sourceProject = (message.data as { projectName?: string } | undefined)?.projectName
        if (sourceProject) {
            const currentProject = getState().auth?.application?.projectName
            const isStatusUpdate =
                action === UpdaterActionTypes.UPDATER_DRIVER_STATUS_DELTA || action === UpdaterActionTypes.UPDATER_DRIVER_DISPATCH_STATUS_UPDATE
            if (sourceProject !== currentProject && (currentProject || isStatusUpdate)) return true
        }
        if (action === UPDATER_TASK_UPDATE_ACTION) {
            const taskMessage = message.data as UpdaterTaskUpdateMessage
            if (typeof window !== 'undefined' && taskMessage.loadId) {
                window.dispatchEvent(new CustomEvent('updater:task-update', { detail: taskMessage }))
            }
            return true
        }

        switch (message.data.action) {
            case UpdaterActionTypes.UPDATER_LOAD_NOTES_UPDATE: {
                handleLoadNotesUpdate(message.data, dispatch, getState)
                break
            }
            case UpdaterActionTypes.UPDATER_DRIVER_NOTES_UPDATE: {
                if (message.data.note) {
                    applyDriverNoteUpdate({ note: message.data.note, deleted: message.data.deleted === true })
                    break
                }
                invalidateDriverNotes(message.data.driverIds ?? (message.data.driverId ? [message.data.driverId] : []))
                break
            }
            case UpdaterActionTypes.UPDATER_LOAD_UPDATE: {
                const { load } = message.data
                enqueueLoadUpdate(load, dispatch, getState)
                break
            }

            case UpdaterActionTypes.UPDATER_LOAD_TAGS_UPDATE: {
                for (const load of message.data.loads) enqueueLoadUpdate(load, dispatch, getState)
                break
            }

            case UpdaterActionTypes.UPDATER_PAYROLL_PLACEMENTS_UPDATE: {
                // A hidden column is refreshed by the next list fetch when enabled; avoid queuing invisible row updates.
                if (!getState().loads.includePayrollPlacement) return true
                // Many loads in one message after a payroll batch changed; each goes through the same queue as a single load update.
                const { placements } = message.data as IUpdaterPayrollPlacementsUpdateMessage
                const { upcomingLoads, dispatchedLoads, inTransitLoads, historyLoads } = getState().loads
                const visibleIds = new Set([...upcomingLoads, ...dispatchedLoads, ...inTransitLoads, ...historyLoads].map((load) => load.id))
                for (const { loadId, payrollPlacement } of placements) {
                    if (visibleIds.has(loadId)) enqueueLoadUpdate({ id: loadId, payrollPlacement }, dispatch, getState)
                }
                break
            }

            case UpdaterActionTypes.UPDATER_DRIVER_STATUS_DELTA:
                handleDriverStatusDelta(message.data, dispatch, getState)
                return true
            case UpdaterActionTypes.UPDATER_DRIVER_DISPATCH_STATUS_UPDATE:
                dispatch(setDriversDispatchStatus({ driverIds: message.data.driverIds, dispatchStatusId: message.data.dispatchStatusId }))
                return true
            case UpdaterActionTypes.UPDATER_LOAD_PROCESSING_UPDATE: {
                const processingMessage = message.data as IUpdaterLoadProcessingUpdateMessage
                const { loadId, status, message: processingMsg, error } = processingMessage
                const combinedMessage = processingMsg ?? (error ? String(error) : undefined)

                dispatch(
                    setLoadProcessingStatus({
                        loadId,
                        status: {
                            status,
                            message: combinedMessage,
                        },
                    })
                )

                if (shouldShowNoEldUpdateToast(status, combinedMessage)) {
                    showError(combinedMessage)
                }
                // Timeout is handled by useLoadProcessingStatus hook
                break
            }

            case UpdaterActionTypes.UPDATER_RATECON_PROCESSING_UPDATE: {
                const msg = message.data as IUpdaterRateconProcessingUpdateMessage
                if (msg.status === 'processing') {
                    const snackbarId = showInfo(`Processing rate confirmation${msg.fileName ? ': ' + msg.fileName : ''}...`, 30000)
                    if (snackbarId) rateconProcessingSnackbars.set(msg.processingId, snackbarId)
                } else if (msg.status === 'success') {
                    const processingSnackbarId = rateconProcessingSnackbars.get(msg.processingId)
                    if (processingSnackbarId) {
                        dismissSnackbar(processingSnackbarId)
                        rateconProcessingSnackbars.delete(msg.processingId)
                    }
                    showSuccess(msg.message || 'Rate confirmation processed successfully', 6000)
                    dispatch(setShouldFetchLoads(true))
                } else if (msg.status === 'error') {
                    const processingSnackbarId = rateconProcessingSnackbars.get(msg.processingId)
                    if (processingSnackbarId) {
                        dismissSnackbar(processingSnackbarId)
                        rateconProcessingSnackbars.delete(msg.processingId)
                    }
                    showError(msg.message || 'Failed to process rate confirmation', 8000)
                }
                break
            }

            case UpdaterActionTypes.UPDATER_TRIP_UPDATE: {
                const { trip, updateType } = message.data as IUpdaterTripUpdateMessage
                if (updateType === UpdaterTripUpdateTypes.TRIP_UPDATED) {
                    dispatch(addTrips([trip]))

                    const stateAfter = getState()

                    // Also add the trip to the appropriate tab's trip list so it gets displayed
                    const currentTab = stateAfter.loads.activeTab
                    const currentTabTripIds = stateAfter.loads.tabTripIds[currentTab] || []

                    // Only add if not already in the list
                    if (!currentTabTripIds.includes(String(trip.id))) {
                        dispatch(
                            setTabTripIds({
                                tab: currentTab,
                                tripIds: [...currentTabTripIds, String(trip.id)],
                            })
                        )
                    }
                }
                break
            }
        }

        return true
    } catch (error) {
        console.error('Error processing updater message:', error)
        return false
    }
}
